import React from 'react';
import { motion } from 'framer-motion';

const Disclaimer: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-800 to-teal-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl font-bold mb-4">Disclaimer</h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Important information about the use of Pincode Pro services
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information Accuracy</h2>
            <p className="text-gray-600 mb-4">
              While we strive to provide accurate and up-to-date information about Indian postal codes and post offices, we cannot guarantee the absolute accuracy of all data. The information provided through Pincode Pro is for general reference purposes only.
            </p>
            <p className="text-gray-600 mb-4">
              Users should verify critical information through official India Post channels or their local post office. We regularly update our database, but there might be a delay in reflecting the most recent changes made by India Post.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Availability</h2>
            <p className="text-gray-600 mb-4">
              We aim to maintain continuous availability of our services, but we do not guarantee uninterrupted access to Pincode Pro. The service may be temporarily unavailable due to maintenance, updates, or factors beyond our control.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Usage Limitations</h2>
            <p className="text-gray-600 mb-4">
              The data provided through our service is intended for personal and non-commercial use. Any commercial use, bulk downloading, or automated querying of our system requires prior written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Links</h2>
            <p className="text-gray-600 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the content, accuracy, or practices of these external sites. The inclusion of any links does not necessarily imply a recommendation or endorsement of the views expressed within them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sources</h2>
            <p className="text-gray-600 mb-4">
              The postal data provided on Pincode Pro is sourced from India Post and other reliable sources. However, we recommend verifying critical information through official channels for the most current and accurate data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Liability</h2>
            <p className="text-gray-600 mb-4">
              Pincode Pro and its operators shall not be liable for any damages or losses resulting from the use of our services, including but not limited to direct, indirect, incidental, punitive, and consequential damages.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Changes to Service</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify, suspend, or discontinue any part of our service without prior notice. We may also update this disclaimer at any time without notification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">General Information</h2>
            <p className="text-gray-600 mb-4">
              The information provided on PincodeIndia (pincodeindia.tag360.in) is for general informational purposes only. While we strive to keep the information up to date and accurate, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Accuracy</h2>
            <p className="text-gray-600 mb-4">
              Our postal code and address data is sourced from official India Post records and is updated regularly. However, postal codes and post office details may change without notice. We recommend verifying critical information through official India Post channels for the most current data.
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2">
              <li>Post office locations and timings may vary</li>
              <li>Service availability may differ by location</li>
              <li>Contact details may be subject to change</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Limitations</h2>
            <p className="text-gray-600 mb-4">
              While we strive to maintain continuous service availability, we cannot guarantee uninterrupted access to our website. Service interruptions may occur due to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2">
              <li>Technical maintenance or updates</li>
              <li>Network or server issues</li>
              <li>Unforeseen circumstances beyond our control</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of PincodeIndia or its content suppliers and is protected by Indian and international copyright laws. The compilation of all content on this site is the exclusive property of PincodeIndia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Usage Terms</h2>
            <p className="text-gray-600 mb-4">
              By using our service, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2">
              <li>Use the service for lawful purposes only</li>
              <li>Not attempt to disrupt or compromise the service</li>
              <li>Not use automated means to access or collect data without permission</li>
              <li>Respect the intellectual property rights of the service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              In no event shall PincodeIndia, its operators, or content providers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use our website and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
            <p className="text-gray-600 mb-4">
              Our website may contain links to external sites. We have no control over the content and nature of these sites and are not responsible for their content or privacy practices. Users access third-party sites at their own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-gray-600">
              For questions or concerns about this disclaimer, please contact us at{' '}
              <a href="mailto:contact@pincodeindia.tag360.in" className="text-purple-600 hover:text-purple-800">
                contact@pincodeindia.tag360.in
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Disclaimer;
